
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Building2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import LenderCard from "@/components/lenders/LenderCard";
import LenderFilters from "@/components/lenders/LenderFilters";
import ViewToggle from "@/components/lenders/ViewToggle";
import { cn } from "@/lib/utils";

interface Lender {
  id: string;
  institution_name: string;
  total_loans_funded: number | null;
  user_id: string | null;
  status: string | null;
  risk_appetite: string | null;
  license_number: string | null;
  min_loan?: number;
  max_loan?: number;
  interest_rate?: string;
  success_rate?: number;
  rating?: string;
}

// Demo data for when no real lenders are available
const demoLenders: Lender[] = [
  {
    id: "1",
    institution_name: "GhanaFirst Finance",
    total_loans_funded: 1250,
    user_id: null,
    status: "active",
    risk_appetite: "conservative",
    license_number: "LIC-2024-001",
    min_loan: 1000,
    max_loan: 50000,
    interest_rate: "12-15",
    success_rate: 95,
    rating: "4.7"
  },
  {
    id: "2",
    institution_name: "Accra Capital Partners",
    total_loans_funded: 850,
    user_id: null,
    status: "active",
    risk_appetite: "moderate",
    license_number: "LIC-2024-002",
    min_loan: 5000,
    max_loan: 100000,
    interest_rate: "14-18",
    success_rate: 92,
    rating: "4.5"
  },
  {
    id: "3",
    institution_name: "West African Growth Fund",
    total_loans_funded: 2100,
    user_id: null,
    status: "active",
    risk_appetite: "aggressive",
    license_number: "LIC-2024-003",
    min_loan: 10000,
    max_loan: 250000,
    interest_rate: "15-22",
    success_rate: 88,
    rating: "4.3"
  }
];

const Lenders = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [riskLevel, setRiskLevel] = useState("all");
  const [view, setView] = useState<"grid" | "list">("grid");

  const { data: lenders, isLoading } = useQuery({
    queryKey: ['lenders'],
    queryFn: async () => {
      // Fetch lenders data directly
      const { data, error } = await supabase
        .from('lenders')
        .select('*')
        .eq('status', 'active');
      
      if (error) {
        console.error('Error fetching lenders:', error);
        toast({
          title: "Error fetching lenders",
          description: error.message,
          variant: "destructive",
        });
        throw error;
      }
      
      // Return demo data if no real data is available
      return (data?.length ? data : demoLenders) as Lender[];
    }
  });

  // Filter lenders based on search term and risk level
  const filteredLenders = lenders?.filter((lender) => {
    const matchesSearch = lender.institution_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRisk = riskLevel === "all" || lender.risk_appetite?.toLowerCase() === riskLevel.toLowerCase();
    return matchesSearch && matchesRisk;
  });

  if (isLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-3xl font-bold">Available Lenders</h1>
          </div>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-64 animate-pulse rounded-lg border bg-card p-6">
              <div className="h-6 bg-muted rounded w-3/4 mb-4"></div>
              <div className="space-y-2">
                <div className="h-4 bg-muted rounded"></div>
                <div className="h-4 bg-muted rounded w-5/6"></div>
                <div className="h-4 bg-muted rounded w-2/3"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(-1)}
            className="shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-3xl font-bold">Available Lenders</h1>
        </div>
        <div className="flex items-center gap-4">
          <p className="text-muted-foreground hidden md:block">
            {filteredLenders?.length || 0} lenders found
          </p>
          <ViewToggle view={view} onViewChange={setView} />
          <Building2 className="h-8 w-8 text-primary" />
        </div>
      </div>

      <LenderFilters 
        searchTerm={searchTerm}
        riskLevel={riskLevel}
        onSearchChange={setSearchTerm}
        onRiskLevelChange={setRiskLevel}
      />

      <div className={cn(
        view === "grid" 
          ? "grid gap-6 md:grid-cols-2 lg:grid-cols-3" 
          : "flex flex-col gap-4"
      )}>
        {filteredLenders?.map((lender) => (
          <LenderCard key={lender.id} lender={lender} view={view} />
        ))}
        
        {filteredLenders?.length === 0 && (
          <div className="col-span-full text-center py-10">
            <p className="text-muted-foreground">
              No lenders found matching your criteria
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Lenders;
